import React from "react";
import { Slot } from "@radix-ui/react-slot";
import classNames from "clsx";
import { headingPropDefs } from "./heading.props";
import {
  extractMarginProps,
  withMarginProps,
  withBreakpoints,
  formatAsId,
} from "../../../helpers";

import type {
  MarginProps,
  GetPropDefTypes,
  NiceIntersection,
} from "../../../helpers";

type HeadingElement = React.ElementRef<"h1">;
type HeadingOwnProps = GetPropDefTypes<typeof headingPropDefs>;
type CommonHeadingProps = NiceIntersection<MarginProps, HeadingOwnProps>;
type HeadingAsChildProps = {
  asChild?: boolean;
  as?: never;
} & React.ComponentPropsWithRef<"h1">;
type HeadingAsProps = {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  asChild?: never;
} & React.ComponentPropsWithRef<"h1">;
type HeadingProps = CommonHeadingProps & (HeadingAsChildProps | HeadingAsProps);
const Heading = React.forwardRef<HeadingElement, HeadingProps>(
  (props, forwardedRef) => {
    const { rest: marginRest, ...marginProps } = extractMarginProps(props);
    const {
      children,
      className,
      asChild = false,
      as: Tag = "h1",
      size = headingPropDefs.size.default,
      weight = headingPropDefs.weight.default,
      align = headingPropDefs.align.default,
      trim = headingPropDefs.trim.default,
      ...headingProps
    } = marginRest;
    return (
      <Slot
        {...headingProps}
        ref={forwardedRef}
        id={formatAsId(String(children))}
        className={classNames(
          "in-heading",
          `in-heading-${Tag.valueOf()}`,
          className,
          size && `in-heading-size-${size.valueOf()}`,
          withBreakpoints(weight, "in-r-weight"),
          withBreakpoints(align, "in-r-ta"),
          withBreakpoints(trim, "in-r-lt"),
          withMarginProps(marginProps)
        )}
      >
        {asChild ? children : <Tag>{children}</Tag>}
      </Slot>
    );
  }
);
Heading.displayName = "Heading";

export { Heading };
export type { HeadingProps };
